import React, { useEffect, useRef, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Input } from "../../components/ui/input";
import {
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "../../components/ui/form";

// ⚙️ FEATURE FLAG: Enable/disable Google Maps cleanup on unmount
// Set to 'true' to cleanup event listeners (prevents navigation blocking)
// Set to 'false' to use old behavior (no cleanup)
// Automatically disabled during Cypress tests to avoid interfering with test selectors
const ENABLE_GOOGLE_MAPS_CLEANUP =
  typeof window !== "undefined" && !(window as any).Cypress;

declare global {
  interface Window {
    google: any;
  }
}

interface AddressAutocompleteProps {
  name: string;
  label: string;
  defaultValue?: string;
  isLoaded: boolean; // ✅ Get `isLoaded` from GoogleMapProvider
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  name,
  label,
  defaultValue = "",
  isLoaded, // ✅ Passed from GoogleMapProvider
}) => {
  const { control, setValue, trigger } = useFormContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (defaultValue) {
      setValue(name, defaultValue, { shouldValidate: true, shouldDirty: true });
    }
  }, [defaultValue, name, setValue]);

  useEffect(() => {
    if (isLoaded && window.google && !initialized) {
      const isCypress =
        typeof window !== "undefined" && !!(window as any).Cypress;
      console.log(
        `🗺️ Initializing Google Maps Autocomplete (Cleanup: ${ENABLE_GOOGLE_MAPS_CLEANUP ? "ON" : "OFF"}, Cypress: ${isCypress})...`
      );

      // ✅ Use `isLoaded` instead of `useJsApiLoader`
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current!,
        {
          types: ["geocode"],
          componentRestrictions: { country: "us" },
        }
      );

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (!place || !place.address_components) return;

        let streetAddress = "";
        let city = "";
        let state = "";
        let zipCode = "";

        place.address_components.forEach((component: any) => {
          const types = component.types;

          if (types.includes("street_number")) {
            streetAddress = component.long_name;
          }
          if (types.includes("route")) {
            streetAddress += ` ${component.long_name}`;
          }
          if (types.includes("locality")) {
            city = component.long_name;
          }
          if (types.includes("administrative_area_level_1")) {
            state = component.short_name;
          }
          if (types.includes("postal_code")) {
            zipCode = component.long_name;
          }
        });

        console.log("✅ Google Autocomplete Data:", {
          streetAddress,
          city,
          state,
          zipCode,
        });

        setValue(name, streetAddress.trim(), {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("city", city, { shouldValidate: true, shouldDirty: true });
        setValue("stateProvince", state, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("zipCode", zipCode, {
          shouldValidate: true,
          shouldDirty: true,
        });

        setTimeout(() => {
          trigger(["address", "city", "stateProvince", "zipCode"]);
        }, 50);
      });

      setInitialized(true);
      console.log("✅ Google Maps Autocomplete initialized");
    }

    // Cleanup function - removes event listeners and prevents memory leaks
    return () => {
      if (
        ENABLE_GOOGLE_MAPS_CLEANUP &&
        autocompleteRef.current &&
        window.google
      ) {
        try {
          console.log("🧹 Cleaning up Google Maps Autocomplete...");

          // Remove all event listeners from the Autocomplete instance
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current
          );

          // Clear the reference
          autocompleteRef.current = null;

          console.log("✅ Google Maps Autocomplete cleaned up successfully");
        } catch (error) {
          console.error("⚠️ Error cleaning up Google Maps:", error);
        }
      } else if (!ENABLE_GOOGLE_MAPS_CLEANUP) {
        console.log("⏭️ Google Maps cleanup DISABLED (using old behavior)");
      }
    };
  }, [isLoaded, initialized, name, setValue, trigger]); // ✅ Watch `isLoaded`

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <FormItem className="mb-4">
          <FormLabel htmlFor={name}>{label}</FormLabel>
          <FormControl>
            <Input
              ref={inputRef}
              type="text"
              id={name}
              placeholder="Start typing your address..."
              value={value || defaultValue}
              onChange={(e) => {
                onChange(e);
                setValue(name, e.target.value, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              disabled={!isLoaded} // ✅ Prevents interaction before API loads
            />
          </FormControl>
          {error && (
            <FormMessage className="text-red-500">{error.message}</FormMessage>
          )}
        </FormItem>
      )}
    />
  );
};

export default AddressAutocomplete;
